import { useCommandPaletteActions } from '@gonfalon/command-palette/actions';
import { enableCommandPaletteViewSaveShortcut } from '@gonfalon/dogfood-flags/dogfood-flags';
import { trackShortcutViewed } from '@gonfalon/shortcuts/internal/analytics';
import { ShortcutsModal } from '@gonfalon/shortcuts/ShortcutsModal';
import { useRouteShortcutType } from '@gonfalon/shortcuts/useRouteShortcutType';
import { DialogTrigger, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { useProjectEntityActionTooltipPlacement } from '../../ProjectEntityLayout/ProjectEntityLayout';

function shortcutDescriptionFor(routeShortcutType: NonNullable<ReturnType<typeof useRouteShortcutType>>): string {
  switch (routeShortcutType) {
    case 'flags':
      return 'Save shortcut to these flags';
    case 'contexts':
      return 'Save shortcut to these contexts';
    case 'segments':
      return 'Save shortcut to these segments';
    case 'flag':
      return 'Save shortcut to this flag';
    case 'view':
      return 'Save shortcut to this view';
    case 'ai-configs':
      return 'Save shortcut to these configs';
    case 'ai-config':
      return 'Save shortcut to this config';
    default:
      return 'Save shortcut to this page';
  }
}

/**
 * Bolt button that opens the palette directly into the "Save shortcut"
 * view, instead of the standalone dialog. Used when the
 * `enableCommandPaletteViewSaveShortcut` flag is on so the button and
 * the palette command converge on a single surface.
 *
 * Lives in its own component so `useCommandPaletteActions` is only
 * called on the flag-on path (the palette provider is always an
 * ancestor in the app, but isolating the hook keeps the flag-off path
 * free of that dependency).
 */
function PaletteShortcutsTrigger({
  description,
  placement,
}: {
  description: string;
  placement: ReturnType<typeof useProjectEntityActionTooltipPlacement>;
}) {
  const { openView } = useCommandPaletteActions();

  return (
    <TooltipTrigger>
      <IconButton
        onPress={() => {
          trackShortcutViewed();
          openView('save-shortcut');
        }}
        size="medium"
        variant="minimal"
        aria-label={description}
        icon="bolt"
      />
      <Tooltip placement={placement}>{description}</Tooltip>
    </TooltipTrigger>
  );
}

export function ShortcutsTrigger() {
  const routeShortcutType = useRouteShortcutType();
  const tooltipPlacement = useProjectEntityActionTooltipPlacement();

  if (!routeShortcutType) {
    return null;
  }

  const shortcutDescription = shortcutDescriptionFor(routeShortcutType);

  if (enableCommandPaletteViewSaveShortcut()) {
    return <PaletteShortcutsTrigger description={shortcutDescription} placement={tooltipPlacement} />;
  }

  return (
    <DialogTrigger>
      <TooltipTrigger>
        <IconButton
          onPress={() => trackShortcutViewed()}
          size="medium"
          variant="minimal"
          aria-label={shortcutDescription}
          icon="bolt"
        />
        <Tooltip placement={tooltipPlacement}>{shortcutDescription}</Tooltip>
      </TooltipTrigger>
      <ShortcutsModal shortcutType={routeShortcutType} />
    </DialogTrigger>
  );
}
